import { useState } from "react";
import { motion } from "framer-motion";
import {
  Webhook,
  Zap,
  GitBranch,
  Bot,
  Play,
  Plus,
  ChevronRight,
} from "lucide-react";
import { APPS } from "@/data/apps";
import { INTEGRATION_META, NODE_DEFS, nodeDef } from "@/data/mockWorkflowNodes";
import { useWorkflow } from "@/store/workflowStore";
import { useHive } from "@/store/useHive";
import {
  AppHeader,
  Badge,
  Card,
  SectionTitle,
} from "@/components/ui/primitives";
import type { HiveWorkflow, HookFlow, WfNodeDef } from "@/types";

const accent = APPS.hooks.accent;

const STEP_STYLE: Record<string, { color: string; icon: any }> = {
  trigger: { color: "#f43f5e", icon: Zap },
  condition: { color: "#f59e0b", icon: GitBranch },
  agent: { color: "#a855f7", icon: Bot },
  action: { color: "#10b981", icon: Play },
};

const STEP_KIND: Record<string, HookFlow["steps"][number]["kind"]> = {
  hook: "trigger",
  process: "agent",
  publish: "action",
};

/** Every workflow IS a hook-triggered chain — this view derives from the org's
 *  real workflows rather than a canned catalog. */
function hookFromWorkflow(wf: HiveWorkflow): HookFlow {
  const order = { hook: 0, process: 1, publish: 2 };
  const steps = [...wf.nodes]
    .filter((n) => !n.disabled)
    .sort((a, b) => {
      const ka = order[nodeDef(a.defKey).kind];
      const kb = order[nodeDef(b.defKey).kind];
      return ka - kb || a.y - b.y;
    })
    .map((n) => ({
      label: n.title,
      kind: STEP_KIND[nodeDef(n.defKey).kind] ?? "action",
    }));
  return {
    id: wf.id,
    name: wf.name,
    trigger: wf.triggerHook,
    runs24h: wf.runsThisWeek,
    status: wf.status === "active" ? "active" : "paused",
    steps,
  };
}

/** Hook catalog grouped by def.group, preserving catalog order. */
function hookCatalog(): { group: string; defs: WfNodeDef[] }[] {
  const groups: string[] = [];
  const byGroup = new Map<string, WfNodeDef[]>();
  NODE_DEFS.filter((d) => d.kind === "hook").forEach((d) => {
    if (!byGroup.has(d.group)) {
      byGroup.set(d.group, []);
      groups.push(d.group);
    }
    byGroup.get(d.group)!.push(d);
  });
  return groups.map((g) => ({ group: g, defs: byGroup.get(g)! }));
}

function HookCatalogCard({ def }: { def: WfNodeDef }) {
  const openApp = useHive((s) => s.openApp);
  const newWorkflowFromHook = useWorkflow((s) => s.newWorkflowFromHook);
  const [busy, setBusy] = useState(false);
  const meta = INTEGRATION_META[def.integration ?? "hive"] ?? INTEGRATION_META.hive;
  return (
    <Card className="group flex items-start gap-3 p-3.5 transition hover:border-app-strong">
      <div
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
        style={{ background: `${meta.color}1a`, color: meta.color }}
      >
        <def.icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-xs font-semibold text-app">{def.label}</span>
          <span
            className="rounded-full border px-1.5 py-px text-[9px] font-medium"
            style={{ borderColor: `${meta.color}44`, color: meta.color }}
          >
            {meta.label}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-app-2">
          {def.description}
        </p>
      </div>
      <button
        disabled={busy}
        onClick={() => {
          setBusy(true);
          newWorkflowFromHook(def.key);
          openApp("workflows");
          setTimeout(() => setBusy(false), 800);
        }}
        className="mt-0.5 flex shrink-0 items-center gap-1 rounded-lg border border-app bg-surface-2 px-2 py-1.5 text-[10px] font-semibold text-app-1 opacity-0 transition group-hover:opacity-100 hover:border-app-strong disabled:opacity-40"
        title="Create a draft workflow triggered by this hook"
      >
        <Plus size={11} /> Build
      </button>
    </Card>
  );
}

export function HooksApp() {
  const workflows = useWorkflow((s) => s.workflows);
  const openApp = useHive((s) => s.openApp);
  const selectWorkflow = useWorkflow((s) => s.selectWorkflow);
  const hooks = workflows.map(hookFromWorkflow);
  const totalRuns = workflows.reduce((n, w) => n + w.runsThisWeek, 0);
  const catalog = hookCatalog();
  const hookTypeCount = catalog.reduce((n, g) => n + g.defs.length, 0);

  return (
    <div className="min-h-full">
      <AppHeader
        icon={Webhook}
        title="Hooks Explorer"
        subtitle={`${hookTypeCount} hook types · ${hooks.length} live trigger chains`}
        accent={accent}
      />

      <div className="grid grid-cols-3 gap-3 px-6 pt-6">
        {[
          { label: "Hook types", value: `${hookTypeCount}` },
          { label: "Active chains", value: `${hooks.filter((h) => h.status === "active").length}` },
          { label: "Executions · 7d", value: totalRuns.toLocaleString() },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-app-2">{s.label}</div>
            <div className="mt-1 text-2xl font-semibold text-app tabular-nums">{s.value}</div>
          </Card>
        ))}
      </div>

      <div className="space-y-3 p-6">
        <SectionTitle>Trigger chains</SectionTitle>
        {hooks.length === 0 && (
          <Card className="p-6 text-center">
            <div className="text-sm font-semibold text-app">No trigger chains yet</div>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-app-2">
              Workflows appear here as hook-triggered chains. Pick a hook from the
              catalog below and hit <span className="font-semibold text-app-1">Build</span>,
              or start from scratch in the Workflow Builder.
            </p>
            <button
              onClick={() => openApp("workflows")}
              className="mt-4 rounded-lg px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
              style={{ background: accent }}
            >
              Open Workflow Builder
            </button>
          </Card>
        )}
        {hooks.map((h, i) => (
          <motion.div
            key={h.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    className="text-sm font-semibold text-app hover:underline"
                    onClick={() => {
                      selectWorkflow(h.id);
                      openApp("workflows");
                    }}
                  >
                    {h.name}
                  </button>
                  <Badge tone={h.status}>{h.status}</Badge>
                </div>
                <span className="text-xs text-app-2">{h.runs24h} runs / 7d</span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {h.steps.map((step, j) => {
                  const st = STEP_STYLE[step.kind];
                  return (
                    <div key={j} className="flex items-center gap-1.5">
                      <motion.div
                        className="flex items-center gap-2 rounded-lg border px-3 py-2"
                        style={{
                          borderColor: `${st.color}44`,
                          background: `${st.color}12`,
                        }}
                        whileHover={{ y: -2 }}
                      >
                        <st.icon size={14} style={{ color: st.color }} />
                        <span className="text-xs font-medium text-app">{step.label}</span>
                      </motion.div>
                      {j < h.steps.length - 1 && (
                        <ChevronRight size={14} className="text-app-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        ))}

        <div className="pt-3">
          <SectionTitle
            right={<span className="text-[11px] text-app-2">hover a hook to build from it</span>}
          >
            Hook catalog
          </SectionTitle>
          <div className="space-y-4">
            {catalog.map(({ group, defs }) => (
              <div key={group}>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-app-2">
                  {group}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {defs.map((def) => (
                    <HookCatalogCard key={def.key} def={def} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
